import React, { useState } from 'react';
import { IDictionary } from '../Interfaces/IDictionary';
import { IDictionaryEntry } from '../Interfaces/IDictionaryEntry';
import { CustomeTextField } from '../generic/CustomeTextField';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  Grid,
  IconButton,
  selectClasses,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
} from '@mui/material';
import { Add, Delete, Edit } from '@mui/icons-material';
import { DictionaryEntryEdit } from './DictionaryEntryEdit';
import { SaveButton } from '../generic/SaveButton';

import { useAlert } from '../context/AlertContext';
import {
  useDeleteDictionaryEntry,
  useWriteDictionaryEntry,
} from '../hooks/reactQuerys/DictionaryEntryQueries';
import { useWriteDictionary } from '../hooks/reactQuerys/DictionaryQueries';

interface IProps {
  editDictionary: IDictionary;
  setEditDictionary: React.Dispatch<React.SetStateAction<IDictionary | null>>;
  dictionaryEntries: IDictionaryEntry[];
  refetchDictionary: () => void;
  refetchDictionaryEntry: () => void;
  enableInstantSave: boolean;
}

export const DictionaryEdit: React.FC<IProps> = ({
  editDictionary,
  setEditDictionary,
  dictionaryEntries,
  refetchDictionary,
  refetchDictionaryEntry,
  enableInstantSave,
}) => {
  const { showAlert } = useAlert();
  const { writeDictionaryEntry, isPendingDictionaryEntry } =
    useWriteDictionaryEntry();
  const { writeDictionary, isPendingDictionary } = useWriteDictionary();
  const { deleteDictionaryEntry, isPendingDeleteDictionaryEntry } =
    useDeleteDictionaryEntry();

  const editDictionaryEntries = dictionaryEntries.filter(
    dictEntry => dictEntry.idDictionary === editDictionary.idDictionary
  );
  const [editVariable, setEditVariable] = useState<IDictionaryEntry | null>(
    null
  );

  const handleNew = () => {
    setEditVariable({
      idDictionary: editDictionary.idDictionary,
      idDictionaryEntry: -1,
      Description: '',
      DictionaryEntry: '',
    } as IDictionaryEntry);
  };

  const onSaveEntry = async () => {
    if (editVariable) {
      try {
        await writeDictionaryEntry(editVariable);
        refetchDictionaryEntry();
        showAlert({
          text: 'Erfolgreich gespeichert!',
          severity: 'success',
        });
        setEditVariable(null);
      } catch (error) {
        console.error(error);
        showAlert({
          text: 'Es ist ein Fehler aufgetretten!',
          severity: 'error',
        });
      }
    }
  };

  const onDeleteEntry = async (id: number) => {
    try {
      await deleteDictionaryEntry(id);
      refetchDictionaryEntry();
      showAlert({
        text: 'Erfolgreich gelöscht!',
        severity: 'success',
      });
      setEditVariable(null);
    } catch (error) {
      console.error(error);
      showAlert({
        text: 'Es ist ein Fehler aufgetretten!',
        severity: 'error',
      });
    }
  };

  const onBlureDictionary = async () => {
    if (editDictionary) {
      try {
        let updatedDict = await writeDictionary(editDictionary);
        setEditDictionary(updatedDict);
        refetchDictionary();
        showAlert({
          text: 'Erfolgreich gespeichert!',
          severity: 'success',
        });
        setEditVariable(null);
      } catch (error) {
        console.error(error);
        showAlert({
          text: 'Es ist ein Fehler aufgetretten!',
          severity: 'error',
        });
      }
    }
  };

  return (
    <>
      {editVariable && (
        <Dialog open onClose={() => setEditVariable(null)}>
          <DialogContent>
            <DictionaryEntryEdit
              editDictEntry={editVariable}
              setEditDictEntry={setEditVariable}
              enableInstantSave={enableInstantSave}
            />
          </DialogContent>
          <DialogActions>
            <Button variant='outlined' onClick={() => setEditVariable(null)}>
              Abbruch
            </Button>
            <SaveButton
              isPending={isPendingDictionaryEntry}
              onClick={onSaveEntry}
            />
          </DialogActions>
        </Dialog>
      )}

      <Grid container spacing={2}>
        <Grid item xs={12} sx={{ display: 'flex' }}>
          <CustomeTextField
            attr='Dictionary'
            label='Bezeichnung'
            object={editDictionary}
            setObject={setEditDictionary}
            type='string'
            onBlur={onBlureDictionary}
          />
          <IconButton onClick={handleNew}>
            <Add />
          </IconButton>
        </Grid>
        <Grid item xs={12}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Variable</TableCell>
                <TableCell>Beschreibung</TableCell>
                <TableCell width={90}>Aktion</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {editDictionaryEntries.map(entry => (
                <TableRow>
                  <TableCell>{entry.DictionaryEntry}</TableCell>
                  <TableCell>
                    {entry.Description.length > 10
                      ? entry.Description.slice(0, 10) + '...'
                      : entry.Description}
                  </TableCell>
                  <TableCell>
                    <IconButton
                      size='small'
                      onClick={() => setEditVariable(entry)}
                    >
                      <Edit />
                    </IconButton>
                    <IconButton
                      size='small'
                      onClick={() => onDeleteEntry(entry.idDictionaryEntry)}
                    >
                      <Delete />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Grid>
      </Grid>
    </>
  );
};
